import { useMemo, useEffect, useCallback } from 'react';

import { useSetState } from 'src/hooks/use-set-state';

import axios, { endpoints } from 'src/utils/axios';

import { STORAGE_KEY } from './constant';
import { AuthContext } from '../auth-context';
import { setLocal, isValidToken } from './utils';

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const { state, setState } = useSetState({
    user: null,
    tickets: null,
    loading: true,
  });

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setLocal(accessToken);

        const res = await axios.get(endpoints.auth.me);
        const { user } = res.data;

        if (window.flutter_inappwebview?.callHandler) {
          window.flutter_inappwebview.callHandler('loginButtonPressed', {
            roles: user?.role,
          });
        } else {
          console.warn('❌ Flutter InAppWebView handler not available');
        }

        const ticketCount = await axios.get('auth/fetch/ticket-count');
        const tickets = ticketCount.data;

        const assetCategory = await axios.get('/auth/fetch/category');
        const { categoryMenu } = assetCategory.data;
        const { alertCounts } = assetCategory.data;

        setState({
          user: { ...user, accessToken, ...tickets, categoryMenu, alertCounts },
          loading: false,
        });
      } else {
        setState({ user: null, loading: false });
      }
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  // LOGOUT
  const logout = useCallback(async () => {
    await axios.get(endpoints.auth.logout);
    setLocal(null);
  }, []);

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            role: state.user?.role ?? 'admin',
          }
        : null,
      checkUserSession,
      isRDPRequired: state?.user?.company?.isRDPRequired ?? false,
      isExternalTicketRequired: state?.user?.company?.isExternalTicketRequired ?? false,
      isAmcRequired: state?.user?.company?.isAmcRequired ?? false,
      isSSOLoginRequired: state?.user?.company?.isSSOLoginRequired ?? false,
      isSelfHealChatBotRequired: state?.user?.company?.isSelfHealChatBotRequired ?? false,
      isReportsAiChatBotRequired: state?.user?.company?.isReportsAiChatBotRequired ?? false,
      isDiscoveryRequired: state?.user?.company?.isDiscoveryRequired ?? false,
      discoveryOrgId: state?.user?.company?.discoveryOrgId,
      isAiImageDiscoveryRequired: state?.user?.company?.isAiImageDiscoveryRequired ?? false,
      isCmmsRequired: state?.user?.company?.isCmmsRequired ?? false,
      isSystemMonitoringRequired: state?.user?.company?.isSystemMonitoringRequired ?? false,
      logout,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      isInitialized: !state.loading,
      companyId: state?.user?.companyId,
    }),
    [state.user, state.loading, checkUserSession, logout, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
